import { Dialog, DialogPanel, DialogTitle } from '@headlessui/react';
import { useEffect, useState } from 'react';
import { X, Check } from 'lucide-react';
import { useOrgChartTemplateState, ITemplate } from '@/states/organization-template.state';
import { DefaultButton, SecondaryButton } from '@/components/ui/Button';
import LoadingSkeleton from '@/components/ui/LoadingSkeleton';
import OrgChartTree from './OrgChartTree';
import { buildTreeFromFlatList, transformToD3Tree } from '@/utils/functions/organization.utils';

interface TemplateSelectionModalProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
  tenantId: number;
  initialTemplateId?: number | null;
}

export default function TemplateSelectionModal({
  open,
  onClose,
  onSuccess,
  tenantId,
  initialTemplateId,
}: TemplateSelectionModalProps) {
  const {
    templates,
    selectedTemplate,
    fetchTemplates,
    fetchTemplateDetails,
    applyTemplate,
    isApplying,
    isLoading,
    clearSelectedTemplate,
  } = useOrgChartTemplateState();

  const [activeTemplateId, setActiveTemplateId] = useState<number | null>(null);

  useEffect(() => {
    if (open) {
      fetchTemplates();
      setActiveTemplateId(initialTemplateId ?? null);
      clearSelectedTemplate();
    }
  }, [open, fetchTemplates, clearSelectedTemplate, initialTemplateId]);

  useEffect(() => {
    if (activeTemplateId) {
      fetchTemplateDetails(activeTemplateId);
    }
  }, [activeTemplateId, fetchTemplateDetails]);

  const handleApply = async () => {
    if (!activeTemplateId) return;

    try {
      await applyTemplate(activeTemplateId, tenantId);
      onSuccess();
      onClose();
    } catch {
      // applyTemplate() already surfaces the error via errorHandler in the store;
      // stop here so onSuccess()/onClose() don't run when apply failed.
      return;
    }
  };

  return (
    <Dialog open={open} onClose={onClose} className="relative z-50">
      <div className="fixed inset-0 bg-black/40" aria-hidden="true" />
      <div className="fixed inset-0 flex items-center justify-center p-4">
        <DialogPanel className="bg-white rounded-xl shadow-xl max-w-4xl w-full max-h-[90vh] flex flex-col overflow-hidden">
          <div className="flex justify-between items-center p-5 border-b">
            <DialogTitle className="text-xl font-bold text-slate-800">Pilih Template Struktur Organisasi</DialogTitle>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-600 transition-colors">
              <X size={24} />
            </button>
          </div>

          <div className="flex flex-1 overflow-hidden">
            {/* Sidebar List */}
            <div className="w-1/3 border-r overflow-y-auto bg-gray-50 p-4 space-y-3">
              {isLoading && !templates.length ? (
                <div className="space-y-3">
                  <LoadingSkeleton className="h-16 w-full rounded-lg" />
                  <LoadingSkeleton className="h-16 w-full rounded-lg" />
                  <LoadingSkeleton className="h-16 w-full rounded-lg" />
                </div>
              ) : (
                templates.map((template: ITemplate) => (
                  <div
                    key={template.id}
                    onClick={() => setActiveTemplateId(template.id)}
                    className={`
                      p-4 rounded-lg cursor-pointer border transition-all duration-200
                      ${
                        activeTemplateId === template.id
                          ? 'bg-blue-50 border-blue-500 shadow-sm'
                          : 'bg-white border-gray-200 hover:border-blue-300 hover:bg-slate-50'
                      }
                    `}
                  >
                    <h4
                      className={`font-semibold ${
                        activeTemplateId === template.id ? 'text-blue-700' : 'text-slate-700'
                      }`}
                    >
                      {template.name}
                    </h4>
                    {/* Basic description snippet if needed */}
                  </div>
                ))
              )}
              {!isLoading && templates.length === 0 && (
                <p className="text-center text-gray-500 mt-10">Tidak ada template tersedia.</p>
              )}
            </div>

            {/* Main Content Details */}
            <div className="w-2/3 p-6 overflow-y-auto">
              {activeTemplateId ? (
                selectedTemplate ? (
                  <div className="space-y-6">
                    <div>
                      <h3 className="text-2xl font-bold text-slate-800 mb-2">{selectedTemplate.template.name}</h3>
                      <p className="text-gray-600 leading-relaxed">{selectedTemplate.template.description}</p>
                    </div>

                    <div className="rounded-lg overflow-hidden border h-[400px] bg-gray-50 relative">
                      {(() => {
                        if (!selectedTemplate?.nodes?.length)
                          return (
                            <div className="flex items-center justify-center h-full text-gray-400">
                              No preview available
                            </div>
                          );

                        const rootNode = buildTreeFromFlatList(selectedTemplate.nodes);
                        const d3Node = transformToD3Tree(rootNode);

                        if (!d3Node)
                          return (
                            <div className="flex items-center justify-center h-full text-gray-400">
                              Invalid template structure
                            </div>
                          );

                        return (
                          <OrgChartTree
                            isLoading={false}
                            treeData={[d3Node]}
                            onSelect={() => undefined}
                            viewMode="diagram"
                            initialZoom={0.5}
                          />
                        );
                      })()}
                    </div>

                    <div>
                      <h4 className="font-semibold text-slate-700 mb-3">
                        Preview Posisi ({selectedTemplate.nodes.length})
                      </h4>
                      {/* ... unchanged list ... */}
                      <div className="bg-slate-50 rounded-lg border p-4 max-h-60 overflow-y-auto">
                        <ul className="space-y-2">
                          {selectedTemplate.nodes.map(node => (
                            <li key={node.id} className="text-sm flex items-center gap-2 text-slate-700">
                              <div className="w-1.5 h-1.5 rounded-full bg-slate-400"></div>
                              {node.name}
                            </li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="space-y-4">
                    <LoadingSkeleton className="h-8 w-1/2" />
                    <LoadingSkeleton className="h-32 w-full" />
                    <LoadingSkeleton className="h-40 w-full" />
                  </div>
                )
              ) : (
                <div className="h-full flex flex-col items-center justify-center text-gray-400">
                  <p>Pilih template dari daftar di sebelah kiri untuk melihat detail.</p>
                </div>
              )}
            </div>
          </div>

          <div className="p-5 border-t bg-gray-50 flex justify-end gap-3">
            <SecondaryButton onClick={onClose} disabled={isApplying}>
              Batal
            </SecondaryButton>
            <DefaultButton
              onClick={handleApply}
              disabled={!activeTemplateId || isApplying || isLoading}
              className={`
                flex items-center gap-2
                ${!activeTemplateId || isApplying ? 'opacity-50 cursor-not-allowed' : 'hover:bg-primary/90'}
              `}
            >
              {isApplying ? (
                'Menerapkan...'
              ) : (
                <>
                  <Check size={18} />
                  Gunakan Template
                </>
              )}
            </DefaultButton>
          </div>
        </DialogPanel>
      </div>
    </Dialog>
  );
}
